import { Component, computed, inject, input } from '@angular/core';
import { rxResource } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { KENDO_STACKLAYOUT } from '@progress/kendo-angular-layout';
import { AbstractManageComponent } from '@unity/abstract/abstract-manage.component';
import { CommunitiesClient, CommunityDetailedDto, CommunityUpdateDto, TransitionRequestDto, UnityWorkflowState } from '@unity/clients';
import { TextboxControlComponent } from '@unity/forms/textbox-control/textbox-control.component';
import { FormLayoutComponent } from '@unity/layouts/form-layout/form-layout.component';
import { PageContainerComponent } from '@unity/layouts/page-container/page-container.component';
import { ActionSharedComponent } from '@unity/shared/composed/action-shared/action-shared.component';
import { PanelManageSharedComponent } from '@unity/shared/composed/panel-manage-shared/panel-manage-shared.component';
import { BannerAlertSharedComponent } from '@unity/shared/presentation/banner-alert-shared/banner-alert-shared.component';
import { WorkflowSelectorSharedComponent } from '@unity/shared/workflow-selector-shared/workflow-selector-shared.component';
import { optionalStringFormControl, stringFormControl } from '@unity/utils/form-controls';
import { QuickActions } from '@unity/utils/quick-actions';
import { FormGroupControlSchema, UnityEntitlementPath } from '@unity/utils/types';
import { EMPTY } from 'rxjs';

class CommunityUpdateSchema implements FormGroupControlSchema<CommunityUpdateDto> {
  public name: FormControl<string>;
  public area_name: FormControl<string | null>;

  public constructor(community?: CommunityDetailedDto) {
    this.name = stringFormControl({ disabled: true, value: community?.name });
    this.area_name = optionalStringFormControl({ disabled: true, value: community?.community_area_name });
  }
}

@Component({
  selector: 'unity-manage-community-page',
  standalone: true,
  imports: [
    KENDO_STACKLAYOUT,
    ReactiveFormsModule,
    PageContainerComponent,
    TextboxControlComponent,
    FormLayoutComponent,
    BannerAlertSharedComponent,
    PanelManageSharedComponent,
    WorkflowSelectorSharedComponent,
    ActionSharedComponent,
  ],
  templateUrl: './manage-community-page.component.html',
  styleUrl: './manage-community-page.component.scss',
})
export class ManageCommunityPageComponent extends AbstractManageComponent<CommunityDetailedDto, CommunityUpdateDto, CommunityUpdateSchema> {
  private readonly communitiesClient = inject(CommunitiesClient);

  public readonly $communityId = input.required({ alias: 'communityId', transform: (value: string) => parseInt(value) });

  protected override mainResource = rxResource({
    params: () => {
      const resourceId = this.$resourceId();
      return resourceId ? { resourceId } : undefined;
    },
    stream: ({ params: { resourceId } }) => this.communitiesClient.getOneAsDetailed(resourceId),
  });

  protected override updateResourceFn$ = (resourceId: number, form: FormGroup<CommunityUpdateSchema>) =>
    this.communitiesClient.updateOne(resourceId, form.getRawValue());

  protected override deleteResourceFn$ = (_: number) => EMPTY; // No-op

  protected override availableTransitionsFn$ = (resourceId: number) => this.availableTransitionsClient.getAllForCommunityAsMini(resourceId);

  protected override doTransitionFn$ = (resourceId: number, transition: TransitionRequestDto) =>
    this.transitionsClient.setForCommunity(resourceId, transition);

  protected override newControlsFn(dto?: CommunityDetailedDto) {
    return new CommunityUpdateSchema(dto);
  }

  protected override editEntitlement: UnityEntitlementPath = 'Community:Update';
  protected override transitionEntitlement: UnityEntitlementPath = 'Community:Transition';
  protected override deleteEntitlement: UnityEntitlementPath = 'Community:Delete';
  protected override resourceName = 'Community';
  protected override $resourceId = this.$communityId;
  protected override readonly $contentHeader = computed(() => `Community: ${this.$resource()?.name}`);

  protected readonly $isDisabled = computed(() => this.$resource()?.current_state.enum_value == UnityWorkflowState.Disabled);

  protected returnToDashboardAction = QuickActions.ReturnToDashboard;
}
